import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Chip,
  Paper,
  Tabs,
  Tab,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Fade,
  Grid
} from '@mui/material';
import { CheckCircle, ChevronLeft, ChevronRight, Category } from '@mui/icons-material';
import PageHeader from '../components/headers/Header';

// Import your policies
import { policies } from "../policies";

// Temporary policies for demonstration - remove this when importing


const PolicyViewer = () => {
  const [selectedPolicyIndex, setSelectedPolicyIndex] = useState(0);
  const [fadeIn, setFadeIn] = useState(true);

  const currentPolicy = policies[selectedPolicyIndex];

  const handlePolicyChange = (index) => {
    setFadeIn(false);
    setTimeout(() => {
      setSelectedPolicyIndex(index);
      setFadeIn(true);
    }, 200);
  };

  const handleNext = () => {
    if (selectedPolicyIndex < policies.length - 1) {
      handlePolicyChange(selectedPolicyIndex + 1);
    }
  };

  const handlePrev = () => {
    if (selectedPolicyIndex > 0) {
      handlePolicyChange(selectedPolicyIndex - 1);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const renderSection = (section, index) => {
    return (
      <Box key={index} sx={{ mb: 4 }}>
        {section.heading && (
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: '#1a237e',
              mb: 2,
              pb: 1,
              borderBottom: '3px solid #3f51b5',
              display: 'inline-block'
            }}
          >
            {section.heading}
          </Typography>
        )}

        {section.subheading && (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: '#303f9f',
              mt: 2,
              mb: 1.5
            }}
          >
            {section.subheading}
          </Typography>
        )}

        {section.text && (
          <Typography
            variant="body1"
            color="text.primary"
            sx={{
              lineHeight: 1.8,
              mb: 2,
              fontSize: '1.05rem'
            }}
          >
            {section.text}
          </Typography>
        )}

        {section.list && (
          <List sx={{ pl: 2 }}>
            {section.list.map((item, idx) => (
              <ListItem key={idx} sx={{ py: 0.5, alignItems: 'flex-start' }}>
                <CheckCircle sx={{ color: '#4caf50', mr: 1.5, mt: 0.5, fontSize: 20 }} />
                <ListItemText
                  primary={item}
                  primaryTypographyProps={{
                    sx: { color: '#424242', lineHeight: 1.7, fontSize: '1rem' }
                  }}
                />
              </ListItem>
            ))}
          </List>
        )}

        {section.subsections && section.subsections.map((subsection, subIdx) => (
          <Box key={subIdx} sx={{ ml: 3, mt: 2, pl: 2, borderLeft: '3px solid #e3f2fd' }}>
            {subsection.subheading && (
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  color: '#1976d2',
                  mb: 1
                }}
              >
                {subsection.subheading}
              </Typography>
            )}

            {subsection.text && (
              <Typography
                variant="body1"
                color="text.primary"
                sx={{
                  lineHeight: 1.8,
                  mb: 1.5,
                  fontSize: '1rem'
                }}
              >
                {subsection.text}
              </Typography>
            )}

            {subsection.list && (
              <List sx={{ pl: 1 }}>
                {subsection.list.map((item, listIdx) => (
                  <ListItem key={listIdx} sx={{ py: 0.3, alignItems: 'flex-start' }}>
                    <CheckCircle sx={{ color: '#66bb6a', mr: 1.5, mt: 0.3, fontSize: 18 }} />
                    <ListItemText
                      primary={item}
                      primaryTypographyProps={{
                        sx: { color: '#424242', lineHeight: 1.7, fontSize: '0.95rem' }
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        ))}
      </Box>
    );
  };

  const renderImage = (image, index) => {
    return (
      <Box key={index} sx={{ mb: 4, textAlign: 'center' }}>
        <img
          src={image.src}
          alt={image.caption || `Policy Image ${index + 1}`}
          style={{ maxWidth: '100%', height: 'auto' }}
        />
      </Box>
    );
  };

  return (
    <Box sx={{ bgcolor: 'transparent', minHeight: '100vh', pb: 4 }}>
      {/* Top Navigation Bar */}
      <Paper
        elevation={2}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1000,
          bgcolor: 'background.paper',
          borderRadius: 3
        }}
      >
        {/* <PageHeader title="Office Policies Full View" subtitle="Explore available company policies" /> */}
        <Container maxWidth="xl" sx={{ mt: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, mb: 2, mt: 2, p:2}}>
            <IconButton
              onClick={handlePrev}
              disabled={selectedPolicyIndex === 0}
              sx={{
                bgcolor: selectedPolicyIndex === 0 ? 'action.disabledBackground' : 'primary.main',
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' },
                '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'text.disabled' }
              }}
            >
              <ChevronLeft />
            </IconButton>

            <Chip
              label={`${selectedPolicyIndex + 1} of ${policies.length}`}
              sx={{
                bgcolor: 'primary.light',
                color: 'primary.dark',
                fontWeight: 600,
                px: 1
              }}
            />

            <IconButton
              onClick={handleNext}
              disabled={selectedPolicyIndex === policies.length - 1}
              sx={{
                bgcolor: selectedPolicyIndex === policies.length - 1 ? 'action.disabledBackground' : 'primary.main',
                color: 'white',
                '&:hover': { bgcolor: 'primary.dark' },
                '&.Mui-disabled': { bgcolor: 'action.disabledBackground', color: 'text.disabled' }
              }}
            >
              <ChevronRight />
            </IconButton>
          </Box>

          {/* Tabs for Policy Selection */}
          <Tabs
            value={selectedPolicyIndex}
            onChange={(e, newValue) => handlePolicyChange(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              borderTop: '1px solid #e0e0e0',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '0.95rem',
                fontWeight: 500,
                minHeight: 56,
                color: 'text.secondary'
              },
              '& .Mui-selected': {
                color: 'primary.main',
                fontWeight: 700
              },
              '& .MuiTabs-indicator': {
                height: 3,
                bgcolor: 'primary.main'
              }
            }}
          >
            {policies.map((policy, index) => (
              <Tab
                key={policy.id}
                label={policy.title}
              />
            ))}
          </Tabs>
        </Container>
      </Paper>

      {/* Policy Content */}
      <Container sx={{ minWidth: '100%', mt: 4 }}>
        <Fade in={fadeIn} timeout={400}>
          <Card
            elevation={3}
            sx={{
              width: '100%',
              borderRadius: 3,
              overflow: 'hidden'
            }}
          >
            {/* Policy Header */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #ede8f5 0%, #adbbda 100%)',
                color: 'white',
                p: 4
              }}
            >
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  mb: 2,
                  textShadow: '2px 2px 4px rgba(0,0,0,0.2)'
                }}
              >
                {currentPolicy.title}
              </Typography>

              <Grid container spacing={2}>
                {/* <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarToday sx={{ fontSize: 20 }} />
                    <Typography variant="body2">
                      <strong>Start:</strong> {formatDate(currentPolicy.startDate)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarToday sx={{ fontSize: 20 }} />
                    <Typography variant="body2">
                      <strong>End:</strong> {formatDate(currentPolicy.endDate)}
                    </Typography>
                  </Box>
                </Grid> */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Category sx={{ color: '#7091e6', fontSize: 20 }} />
                    <Chip
                      label={currentPolicy.category.replace('_', ' ')}
                      size="small"
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.3)',
                        color: '#7091e6',
                        fontWeight: 600
                      }}
                    />
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Policy Body */}
            {currentPolicy.content.sections && <CardContent sx={{ p: 4 }}>
              {currentPolicy.content.sections.map((section, index) => renderSection(section, index))}
            </CardContent>
            }
            {/* Policy Body */}
            {currentPolicy.content.images && (
              <CardContent sx={{ p: 4 }}>
                {currentPolicy.content.images.map((image, index) => renderImage(image, index))}
              </CardContent>
            )}
          </Card>
        </Fade>
      </Container>
    </Box>
  );
};

export default PolicyViewer;